/**
 * 文件相关 API
 */
import { request } from '.';
import { AxiosRequestConfig } from 'axios';
import { toUnderScoreCase } from '@/utils';
import { PaginationParams } from '.';
import { File } from '@/interfaces';
import { FileSafeStatuses } from '@/constants';

/** 获取项目中文件列表的请求数据 */
interface GetProjectFilesParams {
  target?: string;
  word?: string;
}
/** 获取项目中文件列表 */
const getProjectFiles = ({
  projectID,
  params,
  configs,
}: {
  projectID: string;
  params?: GetProjectFilesParams & PaginationParams;
  configs?: AxiosRequestConfig;
}) => {
  return request<File[]>({
    method: 'GET',
    url: `/v1/projects/${projectID}/files`,
    params: toUnderScoreCase(params),
    ...configs,
  });
};

/** 获取文件的请求数据 */
interface GetFileParams {
  target?: string;
}
export interface GetFileReturn extends File {
  projectID: string;
}
/** 获取文件 */
const getFile = ({
  fileID,
  params,
  configs,
}: {
  fileID: string;
  params?: GetFileParams;
  configs?: AxiosRequestConfig;
}) => {
  return request<GetFileReturn>({
    method: 'GET',
    url: `/v1/files/${fileID}`,
    params: toUnderScoreCase(params),
    ...configs,
  });
};

/** 删除文件 */
const deleteFile = ({
  id,
  configs,
}: {
  id: string;
  configs?: AxiosRequestConfig;
}) => {
  return request({
    method: 'DELETE',
    url: `/v1/files/${id}`,
    ...configs,
  });
};

/** 获取项目中文件列表的请求数据 */
interface AdminGetFilesParams {
  safeStatus?: FileSafeStatuses[];
}
/** 获取项目中文件列表 */
const adminGetFiles = ({
  params,
  configs,
}: {
  params?: AdminGetFilesParams & PaginationParams;
  configs?: AxiosRequestConfig;
}) => {
  return request<File[]>({
    method: 'GET',
    url: `/v1/admin/files`,
    params: toUnderScoreCase(params),
    ...configs,
  });
};

const adminSafeCheck = ({
  safeFileIDs,
  unsafeFileIDs,
  configs,
}: {
  safeFileIDs: string[];
  unsafeFileIDs: string[];
  configs?: AxiosRequestConfig;
}) => {
  return request({
    method: 'PUT',
    url: `/v1/admin/files/safe-status`,
    data: toUnderScoreCase({
      safeFiles: safeFileIDs,
      unsafeFiles: unsafeFileIDs,
    }),
    ...configs,
  });
};

export interface EditFileData {
  translator?: string;
  proofreader?: string;
  typesetter?: string;
  /** 原作者署名；空串表示清空（等同撤回） */
  originalAuthor?: string;
  /** 文件重命名（含后缀）；后端权威校验重名/后缀 */
  name?: string;
}
const editFile = ({
  id,
  data,
  configs,
}: {
  id: string;
  data: EditFileData & { replaceTranslator?: boolean };
  configs?: AxiosRequestConfig;
}) => {
  return request({
    method: 'PUT',
    url: `/v1/files/${id}`,
    data: toUnderScoreCase(data),
    ...configs,
  });
};

export interface APIFileSearchItem {
  id: string;
  name: string;
  project_id: string;
  project_name: string;
  project_set_id: string;
  project_set_name: string;
  team_id: string;
  team_name: string;
  can_access: boolean;
}
/** 跨组（团队）文件搜索 */
const searchFiles = ({
  word,
  limit,
  configs,
}: {
  word: string;
  limit?: number;
  configs?: AxiosRequestConfig;
}) => {
  return request<APIFileSearchItem[]>({
    method: 'GET',
    url: `/v1/files/search`,
    params: { word, limit },
    ...configs,
  });
};

export interface APIMoveTargetProject {
  id: string;
  name: string;
}
/** 获取同一项目集下的其它项目（移动目标） */
const getMoveTargetProjects = ({
  projectID,
  configs,
}: {
  projectID: string;
  configs?: AxiosRequestConfig;
}) => {
  return request<APIMoveTargetProject[]>({
    method: 'GET',
    url: `/v1/projects/${projectID}/move-target-projects`,
    ...configs,
  });
};

/** 从社交媒体/外部链接下载图片到项目 */
export interface APIFileImportFromUrl {
  url: string;
  source: 'twitter' | 'twitter_user' | 'bluesky' | 'bluesky_user' | 'pixiv' | 'pixiv_user' | 'external';
}
/** from-url 返回：导入成功的文件列表 + 因重复跳过的文件名列表 */
export interface APIFileFromUrlResult {
  files: string[];
  duplicated: string[];
}
const importFileFromUrl = ({
  projectID,
  data,
  configs,
}: {
  projectID: string;
  data: APIFileImportFromUrl;
  configs?: AxiosRequestConfig;
}) => {
  return request<APIFileFromUrlResult>({
    method: 'POST',
    url: `/v1/projects/${projectID}/files/from-url`,
    data,
    ...configs,
  });
};

/** 「从 X 获取单用户所有图片」：发起任务返回 task_id + total */
export interface APIFileImportTaskStart {
  task_id: string;
  total: number;
}
/** 任务进度轮询返回 */
export interface APIFileImportTaskProgress {
  task_id: string;
  total: number;
  done: number;
  imported: number;
  duplicated: number;
  failed: number;
  failures: Array<{
    index: number;
    filename?: string;
    step?: string;
    reason?: string;
  }>;
  finished: boolean;
  error: string;
}
/** 查询媒体导入任务进度 */
const getImportTaskProgress = ({
  taskID,
  configs,
}: {
  taskID: string;
  configs?: AxiosRequestConfig;
}) => {
  return request<APIFileImportTaskProgress>({
    method: 'GET',
    url: `/v1/files/from-url-task/${taskID}`,
    ...configs,
  });
};

export interface APIFileMoveResult {
  file_id: string;
  name: string;
  status: 'moved' | 'failed' | 'skipped';
  reason: string;
}
/** 批量移动图片到同一项目集下的其它项目 */
const moveFiles = ({
  projectID,
  data,
  configs,
}: {
  projectID: string;
  data: { fileIds: string[]; targetProjectId: string };
  configs?: AxiosRequestConfig;
}) => {
  return request<APIFileMoveResult[]>({
    method: 'PUT',
    url: `/v1/projects/${projectID}/files/move`,
    data: toUnderScoreCase(data),
    ...configs,
  });
};

/** 同源代理获取文件原始内容（字节流）。
 * 外置存储（R2 / imgstore）的直链可能跨域且无 CORS，浏览器 fetch 会被拦截，
 * 故通过后端同源接口读取，用于「自动翻译」取图等场景。 */
const getFileContent = ({
  fileID,
  configs,
}: {
  fileID: string;
  configs?: AxiosRequestConfig;
}) => {
  return request<Blob>({
    method: 'GET',
    url: `/v1/files/${fileID}/content`,
    responseType: 'blob',
    ...configs,
  });
};

/** 撤回校对/嵌字完成 */
interface UndoCompletionData {
  proofreader?: boolean;
  typesetter?: boolean;
  originalAuthor?: boolean;
}
const undoFileCompletion = ({
  id,
  data,
  configs,
}: {
  id: string;
  data: UndoCompletionData;
  configs?: AxiosRequestConfig;
}) => {
  return request({
    method: 'POST',
    url: `/v1/files/${id}/undo-completion`,
    data: toUnderScoreCase(data),
    ...configs,
  });
};

export default {
  getProjectFiles,
  getFile,
  getFileContent,
  deleteFile,
  editFile,
  importFileFromUrl,
  getImportTaskProgress,
  adminGetFiles,
  adminSafeCheck,
  searchFiles,
  getMoveTargetProjects,
  moveFiles,
  undoFileCompletion,
};
